// Abrash — the lightweight in-app frame profiler.
//
// Named for Michael Abrash, whose *Zen of Assembly Language* and *Graphics
// Programming Black Book* taught a generation to measure first ("the best
// optimizer is between your ears"). Console: `profiler`, alias `abrash`.
//
// DevTools tracing is impractical at high (uncapped) frame rates — the trace
// grows to gigabytes and the tracer itself perturbs the measurement. This
// measures per-frame work time in-process (no giant files) and shows a rolling
// summary.
//
// What each number means (read this before quoting them):
// - `sim`/`work` are real CPU time around the fixed-step sim and the loop body.
// - `render` is **CPU command submission** (`performance.now()` around
//   `renderer.draw`). WebGL is asynchronous, so this is the time it takes to
//   *ask* the GPU, not to finish the frame. Useful on its own (uniform /
//   draw-call / state overhead), never evidence that the GPU has headroom.
// - `gpu` is GPU pass time from `EXT_disjoint_timer_query_webgl2`, measured with
//   a small pool of queries so results are attributed to the frame that issued
//   them and each sample is counted exactly once. Results arrive 1..N frames
//   late (shown as `age`); a disjoint event invalidates the whole batch and the
//   overlay reports `gpu stale` until a fresh sample lands. Driver quality
//   varies (some drivers report optimistic times), so treat it as a hint and
//   use the rAF interval as the end-to-end ground truth.
// - `tail` is the part averages hide: p99/max for frame interval and loop work,
//   the % of frames over the work budget, and a hitch rate. `worst` names the
//   dominant stage of the worst frame in the window (or `outside loop` when the
//   gap was not our work at all — compositor/GC/driver).

export interface FrameStats {
  /** ms since the previous loop start (includes any vsync wait). */
  interval: number;
  /** ms spent inside loop() doing work. */
  work: number;
  /** ms spent stepping the sim this frame. */
  sim: number;
  /** ms spent issuing `renderer.draw` (CPU submission, not GPU completion). */
  render: number;
  /** ms spent reconciling against the server this frame. */
  reconcile?: number;
  /** ms spent on network send/drain this frame. */
  net?: number;
  /** ms spent on per-frame HUD/DOM work (movement HUD, name tags). */
  hud?: number;
  /** ms spent in the (framerate-independent) subsystem scheduler. */
  tasks?: number;
}

/** One in-flight timer query, tagged with the frame that issued it. */
interface GpuQuery {
  q: WebGLQuery;
  frame: number;
}

/** Fixed-capacity FIFO of numbers with O(1) push and random access. */
class Ring {
  private buf: Float64Array;
  private head = 0;
  count = 0;
  constructor(cap: number) {
    this.buf = new Float64Array(cap);
  }
  push(v: number) {
    this.buf[this.head] = v;
    this.head = (this.head + 1) % this.buf.length;
    if (this.count < this.buf.length) this.count++;
  }
  /** `i = 0` is the oldest retained sample. */
  get(i: number): number {
    const n = this.buf.length;
    return this.buf[(this.head - this.count + i + n) % n];
  }
  clear() {
    this.head = 0;
    this.count = 0;
  }
  capacity(): number {
    return this.buf.length;
  }
}

/**
 * GPU pass timer built on a small pool of queries.
 *
 * A single query in flight would make the result 1..N frames old and (worse)
 * get re-read every frame, so the "average" would really be the last sample
 * copied hundreds of times. A pool keeps the pipeline moving while still
 * attributing every result to exactly one frame.
 */
class GpuTimer {
  private gl: WebGL2RenderingContext;
  private ext: {
    TIME_ELAPSED_EXT: number;
    GPU_DISJOINT_EXT: number;
  } | null;
  private inFlight: GpuQuery[] = [];
  /** Query opened by `begin()` this frame, waiting for `end()`. */
  private open: WebGLQuery | null = null;
  /** Completed results since the last `drain()`, as [ms, delayFrames]. */
  private completed: Array<[number, number]> = [];
  private staleFlag = false;
  private frame = 0;
  /**
   * Deep enough to keep issuing a query every frame even when the driver only
   * surfaces results ~20–30 frames later (ANGLE/Vulkan batches them); a shallow
   * pool would silently drop most frames from the sample.
   */
  private readonly MAX_IN_FLIGHT = 24;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.ext = gl.getExtension("EXT_disjoint_timer_query_webgl2") as GpuTimer["ext"];
  }

  available(): boolean {
    return this.ext !== null;
  }

  /** Advance the frame counter (call once at the start of every profiled frame). */
  beginFrame() {
    this.frame++;
  }

  begin() {
    if (!this.ext || this.inFlight.length >= this.MAX_IN_FLIGHT) return;
    const q = this.gl.createQuery();
    if (!q) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    this.open = q;
    this.inFlight.push({ q, frame: this.frame });
  }

  end() {
    if (!this.ext || !this.open) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.open = null;
  }

  poll() {
    if (!this.ext || this.inFlight.length === 0) return;
    const gl = this.gl;
    // A disjoint flag invalidates *all* pending results (the GPU clock or the
    // timer was reset), so drop the whole batch rather than mixing eras.
    if (gl.getParameter(this.ext.GPU_DISJOINT_EXT)) {
      for (const e of this.inFlight) gl.deleteQuery(e.q);
      this.inFlight.length = 0;
      this.open = null;
      this.completed.length = 0;
      this.staleFlag = true;
      return;
    }
    let i = 0;
    while (i < this.inFlight.length) {
      const e = this.inFlight[i];
      if (gl.getQueryParameter(e.q, gl.QUERY_RESULT_AVAILABLE)) {
        const ns = gl.getQueryParameter(e.q, gl.QUERY_RESULT) as number;
        gl.deleteQuery(e.q);
        this.inFlight.splice(i, 1);
        this.completed.push([ns / 1e6, this.frame - e.frame]);
        this.staleFlag = false;
      } else {
        i++;
      }
    }
  }

  /** Pop results that completed since the last call (each exactly once). */
  drain(): Array<[number, number]> {
    if (this.completed.length === 0) return [];
    const out = this.completed;
    this.completed = [];
    return out;
  }

  /** True when the last poll saw a disjoint event and nothing valid since. */
  stale(): boolean {
    return this.staleFlag;
  }

  inFlightCount(): number {
    return this.inFlight.length;
  }
}

/** The worst frame in the rolling window and what dominated it. */
export interface WorstFrameInfo {
  /** Wall-clock age of this frame, in seconds. */
  ageSec: number;
  /** Frame interval (ms) — the end-to-end number. */
  interval: number;
  /** Loop work in that frame (ms). */
  work: number;
  /** Dominant stage, or "outside loop" when work was a small part of the gap. */
  cause: string;
  /** The dominant stage's ms (0 for "outside loop"). */
  causeMs: number;
  /** Second-largest stage name ("" when none). */
  second: string;
  secondMs: number;
  sim: number;
  render: number;
  net: number;
  hud: number;
  tasks: number;
  reconcile: number;
  other: number;
}

export interface ProfilerStats {
  frames: number;
  gpuAvailable: boolean;
  gpuStale: boolean;
  gpuInFlight: number;
  gpuSamples: number;
  gpuSamplesTotal: number;
  gpuLastMs: number;
  gpuAvgDelayFrames: number;
  /** Loop work (CPU) tail. */
  p50Work: number;
  p99Work: number;
  maxWork: number;
  overBudgetPct: number;
  /** End-to-end frame interval tail (rAF cadence). */
  p50Frame: number;
  p99Frame: number;
  maxFrame: number;
  /** Frames over `max(8 ms, 1.5 x median)` per second. */
  hitchesPerSec: number;
  hitchCount: number;
  worstFrame: WorstFrameInfo | null;
}

function percentile(view: Float64Array, n: number, p: number): number {
  if (n === 0) return 0;
  const i = Math.min(n - 1, Math.max(0, Math.floor(n * p)));
  return view[i];
}

export class Profiler {
  private el: HTMLElement | null;
  private gpu: GpuTimer;
  private enabled = false;
  private readonly CAP = 360; // rolling window (~1s at 360fps)
  // Per-frame samples as parallel rings: allocation-free in the steady state.
  private rInterval = new Ring(this.CAP);
  private rWork = new Ring(this.CAP);
  private rSim = new Ring(this.CAP);
  private rRender = new Ring(this.CAP);
  private rReconcile = new Ring(this.CAP);
  private rNet = new Ring(this.CAP);
  private rHud = new Ring(this.CAP);
  private rTasks = new Ring(this.CAP);
  private rTime = new Ring(this.CAP); // performance.now() of the sample
  /** Scratch for sorting (reused; typed-array sort does not allocate). */
  private scratchA = new Float64Array(this.CAP);
  private scratchB = new Float64Array(this.CAP);
  private rGpu = new Ring(this.CAP);
  private gpuDelay = 0;
  private gpuDelayN = 0;
  private gpuSamplesTotal = 0;
  private frames = 0;
  private budgetMs = 0;
  private lastDom = 0;

  constructor(gl: WebGL2RenderingContext, el: HTMLElement | null) {
    this.el = el;
    this.gpu = new GpuTimer(gl);
  }

  /** Soft main-thread work budget per frame (ms); shown with the overrun %. */
  setBudget(ms: number) {
    this.budgetMs = ms;
  }

  setEnabled(on: boolean) {
    this.enabled = on;
    this.el?.classList.toggle("hidden", !on);
  }
  isEnabled(): boolean {
    return this.enabled;
  }
  gpuAvailable(): boolean {
    return this.gpu.available();
  }
  gpuBegin() {
    if (!this.enabled) return;
    this.gpu.beginFrame();
    this.gpu.begin();
  }
  gpuEnd() {
    if (this.enabled) this.gpu.end();
  }
  gpuPoll() {
    if (this.enabled) this.gpu.poll();
  }

  add(s: FrameStats) {
    if (!this.enabled) return;
    // The very first frame has no previous loop start; don't poison the tail.
    if (!(s.interval > 0)) return;
    this.frames++;
    this.rInterval.push(s.interval);
    this.rWork.push(s.work);
    this.rSim.push(s.sim);
    this.rRender.push(s.render);
    this.rReconcile.push(s.reconcile ?? 0);
    this.rNet.push(s.net ?? 0);
    this.rHud.push(s.hud ?? 0);
    this.rTasks.push(s.tasks ?? 0);
    this.rTime.push(performance.now());

    // GPU: exactly one ring entry per completed query. A disjoint event wipes
    // the window so we never average measurements from an invalidated era.
    if (this.gpu.stale() && this.rGpu.count > 0) {
      this.rGpu.clear();
      this.gpuDelay = 0;
      this.gpuDelayN = 0;
    }
    for (const [ms, delay] of this.gpu.drain()) {
      this.rGpu.push(ms);
      this.gpuDelay += delay;
      this.gpuDelayN++;
      this.gpuSamplesTotal++;
    }
  }

  /**
   * Copy a ring into `scratch` and sort it ascending. The caller owns the
   * scratch buffer, so two sorted views can be live at once.
   */
  private sorted(ring: Ring, scratch: Float64Array): { view: Float64Array; n: number } {
    const n = Math.min(ring.count, scratch.length);
    for (let i = 0; i < n; i++) scratch[i] = ring.get(i);
    const view = scratch.subarray(0, n);
    view.sort();
    return { view, n };
  }

  private avg(ring: Ring): number {
    if (ring.count === 0) return 0;
    let sum = 0;
    for (let i = 0; i < ring.count; i++) sum += ring.get(i);
    return sum / ring.count;
  }

  private maxOf(ring: Ring): number {
    let m = 0;
    for (let i = 0; i < ring.count; i++) {
      const v = ring.get(i);
      if (v > m) m = v;
    }
    return m;
  }

  /**
   * Find the worst frame in the window, name its dominant stage, and classify
   * it: when the loop only did a small part of the gap, the stall happened
   * outside our code (compositor, GC, driver, shader compile).
   */
  private findWorst(now: number): WorstFrameInfo | null {
    if (this.rInterval.count === 0) return null;
    let worst = 0;
    let idx = 0;
    for (let i = 0; i < this.rInterval.count; i++) {
      const v = this.rInterval.get(i);
      if (v > worst) {
        worst = v;
        idx = i;
      }
    }
    const work = this.rWork.get(idx);
    const sim = this.rSim.get(idx);
    const render = this.rRender.get(idx);
    const net = this.rNet.get(idx);
    const hud = this.rHud.get(idx);
    const tasks = this.rTasks.get(idx);
    const reconcile = this.rReconcile.get(idx);
    const other = Math.max(0, work - sim - render - net - hud - tasks - reconcile);
    const stages: Array<[string, number]> = [
      ["sim", sim],
      ["render", render],
      ["net", net],
      ["hud", hud],
      ["tasks", tasks],
      ["recon", reconcile],
      ["other", other],
    ];
    stages.sort((a, b) => b[1] - a[1]);
    const outside = work < worst * 0.5;
    const ageSec = Math.max(0, (now - this.rTime.get(idx)) / 1000);
    return {
      ageSec,
      interval: worst,
      work,
      cause: outside ? "outside loop" : stages[0][0],
      causeMs: outside ? 0 : stages[0][1],
      second: outside ? stages[0][0] : stages.length > 1 ? stages[1][0] : "",
      secondMs: outside ? stages[0][1] : stages.length > 1 ? stages[1][1] : 0,
      sim,
      render,
      net,
      hud,
      tasks,
      reconcile,
      other,
    };
  }

  stats(): ProfilerStats {
    const work = this.sorted(this.rWork, this.scratchA);
    let overCount = 0;
    if (this.budgetMs > 0) {
      for (let i = 0; i < work.n; i++) if (work.view[i] > this.budgetMs) overCount++;
    }
    const over = work.n ? (overCount / work.n) * 100 : 0;
    const frame = this.sorted(this.rInterval, this.scratchB);
    const p50Frame = percentile(frame.view, frame.n, 0.5);
    const hitchLimit = Math.max(8, p50Frame * 1.5);
    let hitchCount = 0;
    let windowMs = 0;
    for (let i = 0; i < this.rInterval.count; i++) {
      const v = this.rInterval.get(i);
      windowMs += v;
      if (v > hitchLimit) hitchCount++;
    }
    const hitchesPerSec = windowMs > 0 ? hitchCount / (windowMs / 1000) : 0;
    return {
      frames: this.frames,
      gpuAvailable: this.gpu.available(),
      gpuStale: this.gpu.stale(),
      gpuInFlight: this.gpu.inFlightCount(),
      gpuSamples: this.rGpu.count,
      gpuSamplesTotal: this.gpuSamplesTotal,
      gpuLastMs: this.rGpu.count ? this.rGpu.get(this.rGpu.count - 1) : 0,
      gpuAvgDelayFrames: this.gpuDelayN ? this.gpuDelay / this.gpuDelayN : 0,
      p50Work: percentile(work.view, work.n, 0.5),
      p99Work: percentile(work.view, work.n, 0.99),
      maxWork: this.maxOf(this.rWork),
      overBudgetPct: over,
      p50Frame,
      p99Frame: percentile(frame.view, frame.n, 0.99),
      maxFrame: this.maxOf(this.rInterval),
      hitchesPerSec: Math.round(hitchesPerSec * 10) / 10,
      hitchCount,
      worstFrame: this.findWorst(performance.now()),
    };
  }

  /** Refresh the on-screen overlay (throttled to ~4 Hz). */
  update(now: number) {
    if (!this.enabled || !this.el) return;
    if (now - this.lastDom < 250) return;
    this.lastDom = now;

    // Work first (scratch A), then frames (scratch B): each view stays valid
    // while its numbers are consumed.
    const work = this.sorted(this.rWork, this.scratchA);
    const p95Work = percentile(work.view, work.n, 0.95);
    const p99Work = percentile(work.view, work.n, 0.99);
    let overCount = 0;
    if (this.budgetMs > 0) {
      for (let i = 0; i < work.n; i++) if (work.view[i] > this.budgetMs) overCount++;
    }
    const over = work.n ? (overCount / work.n) * 100 : 0;

    const frame = this.sorted(this.rInterval, this.scratchB);
    const p50Frame = percentile(frame.view, frame.n, 0.5);
    const p99Frame = percentile(frame.view, frame.n, 0.99);
    const maxFrame = this.maxOf(this.rInterval);

    const intervalAvg = this.avg(this.rInterval);
    const fps = intervalAvg > 0 ? 1000 / intervalAvg : 0;
    const workAvg = this.avg(this.rWork);
    const simAvg = this.avg(this.rSim);
    const renderAvg = this.avg(this.rRender);
    const recAvg = this.avg(this.rReconcile);
    const netAvg = this.avg(this.rNet);
    const hudAvg = this.avg(this.rHud);
    const taskAvg = this.avg(this.rTasks);
    const otherAvg = Math.max(
      0,
      workAvg - simAvg - renderAvg - recAvg - netAvg - hudAvg - taskAvg,
    );
    const maxWork = this.maxOf(this.rWork);

    // Tail: hitches are frames over max(8 ms, 1.5 x the window median). That
    // catches both "one 40 ms stall at 700 fps" and "a dropped frame at 60 fps".
    const hitchLimit = Math.max(8, p50Frame * 1.5);
    let hitchCount = 0;
    let windowMs = 0;
    for (let i = 0; i < this.rInterval.count; i++) {
      const v = this.rInterval.get(i);
      windowMs += v;
      if (v > hitchLimit) hitchCount++;
    }
    const hitchesPerSec = windowMs > 0 ? hitchCount / (windowMs / 1000) : 0;
    const h = this.findWorst(now);

    // GPU percentile uses scratch A again (work numbers are already consumed).
    let gpuLine: string;
    if (!this.gpu.available()) {
      gpuLine = `gpu n/a`;
    } else if (this.rGpu.count === 0) {
      gpuLine = this.gpu.stale() ? `gpu stale` : `gpu …`;
    } else {
      const gpuSorted = this.sorted(this.rGpu, this.scratchA);
      const delayF = this.gpuDelayN ? this.gpuDelay / this.gpuDelayN : 0;
      const delayMs = delayF * intervalAvg;
      gpuLine =
        `gpu ${this.avg(this.rGpu).toFixed(3)}ms n=${this.rGpu.count}` +
        ` p95 ${percentile(gpuSorted.view, gpuSorted.n, 0.95).toFixed(2)}` +
        ` age ${delayMs.toFixed(0)}ms/${delayF.toFixed(0)}f`;
      if (this.gpu.stale()) gpuLine += ` stale`;
    }

    const budgetNote =
      this.budgetMs > 0 ? `  budget ${this.budgetMs.toFixed(0)}ms (over ${over.toFixed(0)}%)` : "";

    let text =
      `Abrash — frame profiler\n` +
      `fps ${fps.toFixed(0).padStart(5)}   work ${workAvg.toFixed(3)}ms` +
      `  (p95 ${p95Work.toFixed(3)}  max ${maxWork.toFixed(2)})` +
      `${budgetNote}\n`;
    text +=
      `sim ${simAvg.toFixed(2)} recon ${recAvg.toFixed(2)} net ${netAvg.toFixed(2)}` +
      ` hud ${hudAvg.toFixed(2)} tasks ${taskAvg.toFixed(2)}` +
      ` render(cpu) ${renderAvg.toFixed(2)} other ${otherAvg.toFixed(2)}\n`;
    text += gpuLine;
    text +=
      `\ntail  work p99 ${p99Work.toFixed(2)} max ${maxWork.toFixed(1)}` +
      ` | frame p99 ${p99Frame.toFixed(2)} max ${maxFrame.toFixed(1)}` +
      ` | hitch ${hitchesPerSec.toFixed(1)}/s`;
    // Only explain a frame when there is actually a hitch: at a healthy
    // steady state the "worst" frame is just a normal 5 ms frame.
    if (h && hitchCount > 0) {
      text +=
        `\nworst ${h.interval.toFixed(1)}ms  ${h.cause}` +
        (h.causeMs > 0 ? ` ${h.causeMs.toFixed(2)}` : "") +
        (h.second ? ` + ${h.second} ${h.secondMs.toFixed(2)}` : "") +
        `  work ${h.work.toFixed(2)}  (-${h.ageSec.toFixed(1)}s)`;
    }
    this.el.textContent = text;
  }
}
